import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { signIn, signUp, sendPasswordReset, completePasswordReset, onPasswordRecovery } from '../AuthContext';
import { APP_NAME, kitEnv } from '../data/kitEnv';

const API = kitEnv('UPLOAD_API', process.env.REACT_APP_UPLOAD_API || '');

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  // 'signin' | 'signup' | 'forgot' | 'reset' (reset = arrived via the emailed
  // recovery link, which lands on /login?reset=1 — App keeps that URL on this
  // card even though the recovery session already counts as signed in).
  const [mode, setMode] = useState(() => (new URLSearchParams(location.search).has('reset') ? 'reset' : 'signin'));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // /login?sso=failed|unsupported — the control plane's one-click sign-in
  // (backend api/authSso.js) bounced; say so instead of a blank form.
  const [error, setError] = useState(() => {
    const sso = new URLSearchParams(location.search).get('sso');
    if (!sso) return null;
    return sso === 'unsupported'
      ? 'Single sign-on is not available on this instance yet — sign in with your instance account, or use “Forgot password?” to set one.'
      : 'Single sign-on link expired or was already used — go back to nventr.io and open the Dev Console again, or sign in below.';
  });
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);
  // Membership policy (backend GET /auth/policy): 'closed' hides the
  // create-account path entirely, 'domains' says who may use it. The server
  // enforces it either way — this only keeps the card honest.
  const [policy, setPolicy] = useState({ signup: 'closed', domains: [] });

  // supabase plane: the emailed recovery link opens /login with a recovery
  // session; switch to the set-new-password form.
  useEffect(() => onPasswordRecovery(() => { setMode('reset'); setError(null); setNotice(null); }), []);
  useEffect(() => {
    let live = true;
    fetch(`${API}/auth/policy`).then((r) => (r.ok ? r.json() : null)).then((p) => { if (live && p?.signup) setPolicy(p); }).catch(() => {});
    return () => { live = false; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      if (mode === 'signup') {
        // Same answer the server gives (supabase: the /auth/v1/signup gate;
        // firebase: ensureSeat) — just earlier, before an account is created.
        const domain = email.trim().toLowerCase().split('@')[1] || '';
        if (policy.signup === 'closed') throw new Error('This app is invite-only — ask your administrator for access.');
        if (policy.signup === 'domains' && !policy.domains.includes(domain)) throw new Error(`Sign-up is limited to ${policy.domains.map((d) => `@${d}`).join(', ')} addresses — ask your administrator for access.`);
        await signUp(email, password);
      } else if (mode === 'forgot') {
        await sendPasswordReset(email);
        setNotice(`If an account exists for ${email}, a reset link is on its way. Check your inbox (and spam).`);
      } else if (mode === 'reset') {
        await completePasswordReset(password);
        navigate('/login', { replace: true }); // drop ?reset=1 → App redirects into the app
      } else {
        await signIn(email, password);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const title = { signup: 'Create account', forgot: 'Reset your password', reset: 'Choose a new password' }[mode] || 'Sign in';
  const cta = { signup: 'Create account', forgot: 'Email me a reset link', reset: 'Set new password' }[mode] || 'Sign in';

  return (
    <div className="cs-modal" style={{ maxWidth: 400 }}>
      <div className="cs-brand" style={{ fontSize: 28, marginBottom: 6 }}>
        {APP_NAME}
      </div>
      <h2 style={{ fontSize: 22, marginTop: 0 }}>{title}</h2>

      {error && (
        <div className="cs-empty" style={{ padding: 12, marginBottom: 14, color: 'var(--accent)' }}>
          {error}
        </div>
      )}
      {notice && (
        <div className="cs-empty" style={{ padding: 12, marginBottom: 14 }}>
          {notice}
        </div>
      )}

      <form onSubmit={submit}>
        {mode !== 'reset' && (
          <div className="cs-field">
            <label>Email</label>
            <input
              type="email"
              className="cs-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
          </div>
        )}
        {mode !== 'forgot' && (
          <div className="cs-field">
            <label>{mode === 'reset' ? 'New password' : 'Password'}</label>
            <input
              type="password"
              className="cs-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
            />
          </div>
        )}
        <button type="submit" className="cs-btn cs-btn-primary" style={{ width: '100%' }} disabled={busy}>
          {busy ? 'Please wait…' : cta}
        </button>
      </form>

      <div style={{ textAlign: 'center', marginTop: 16, display: 'flex', flexDirection: 'column', gap: 4 }}>
        {mode === 'signin' && (
          <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => { setMode('forgot'); setError(null); setNotice(null); }}>
            Forgot password?
          </button>
        )}
        {mode !== 'reset' && (policy.signup !== 'closed' || mode !== 'signin') && (
          <button
            className="cs-btn cs-btn-ghost cs-btn-sm"
            onClick={() => { setMode(mode === 'signin' ? 'signup' : 'signin'); setError(null); setNotice(null); }}
          >
            {mode === 'signin' ? 'Need an account? Create one' : 'Back to sign in'}
          </button>
        )}
        {mode === 'signup' && policy.signup === 'domains' && (
          <p className="cs-help" style={{ margin: '4px 0 0' }}>Sign-up is limited to {policy.domains.map((d) => `@${d}`).join(', ')} addresses.</p>
        )}
      </div>
    </div>
  );
}
